/**
 * SportsbookBadge Component
 * Displays a color-coded badge for sportsbooks
 *
 * Features:
 * - Colored dot indicator unique to each sportsbook
 * - Optional text label
 * - Two sizes: sm (default) and md
 */

import { SPORTSBOOKS, type SportsbookConfig } from '@config/constants';
import { SPORTSBOOK_COLORS } from '@parlay-picker/shared/sportsbook';
import type { ReactElement } from 'react';

// =============================================================================
// Types
// =============================================================================

interface SportsbookBadgeProps {
  sportsbook: string;
  showDot?: boolean; // Show colored dot indicator
  showLabel?: boolean; // Show text label
  size?: 'sm' | 'md'; // Badge size
}

// =============================================================================
// Component
// =============================================================================

/**
 * SportsbookBadge - Displays a color-coded badge for a sportsbook
 *
 * Examples:
 * - 'fanduel' → 🔵 FanDuel (blue)
 * - 'draftkings' → 🟢 DraftKings (green)
 * - 'betmgm' → 🟡 BetMGM (amber)
 */
export default function SportsbookBadge({
  sportsbook,
  showDot = true,
  showLabel = true,
  size = 'sm',
}: SportsbookBadgeProps): ReactElement | null {
  const config: SportsbookConfig | undefined = SPORTSBOOKS[sportsbook];
  const colors = SPORTSBOOK_COLORS[sportsbook];

  // Return null for unknown sportsbooks
  if (!config || !colors) {
    return null;
  }

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';

  return (
    <span
      className={`inline-flex items-center gap-1.5 ${colors.bg} ${colors.text} ${sizeClasses} rounded font-medium`}
    >
      {showDot && <span className={`w-2 h-2 rounded-full ${colors.dot}`} />}
      {showLabel && colors.name}
    </span>
  );
}

/**
 * SportsbookDot - Just the colored dot (no label or background)
 * Useful for compact displays where you just want a color indicator
 */
export function SportsbookDot({ sportsbook }: { sportsbook: string }): ReactElement | null {
  const colors = SPORTSBOOK_COLORS[sportsbook];

  if (!colors) {
    return null;
  }

  return <span className={`w-2 h-2 rounded-full ${colors.dot}`} title={colors.name} />;
}
